// packages/i18n/src/embedFormatMessage.ts
//
// The embed's `use-intl/format-message` (docs/embed-size-and-speed.md §3.2, icu-stack). use-intl's translator formats
// every message through that module, which compiles it with intl-messageformat and the formatjs parser: about 33 KB
// in each widget. apps/embed/craco.config.js aliases the module to this file, which formats the ICU subset every
// string a widget fetches uses (`embedMessageSubset.ts`: text, `{name}`, `plural`, `select`) and answers as use-intl
// 4.8.2's own module does for all of them, errors included: the same guards, the same fast path, the same output
// (intl-messageformat 11.1.2's `formatToParts` and `format`, ported) and the same errors. So the translator around it
// (createTranslator, `has`, its onError and its `common.<key>` fallback) behaves as before. A message outside the
// subset fails to compile, INVALID_MESSAGE, as an invalid message does.
//
// Only the embed's webpack build uses it: a TourTask site renders the same storefront with Next and use-intl's own
// formatter. Tests compare the two on every string the widgets fetch, in every locale
// (packages/storefront/src/contexts/storefrontStringsFormat.test.ts, apps/embed/src/entries/widgetStringsFormat.test.ts),
// and on generated messages (embedFormatMessage.test.ts).

import {
	IntlError,
	IntlErrorCode,
	type _createCache,
	type _createIntlFormatters,
	type Formats,
	type RichTagsFunction,
	type RichTranslationValues,
	type Timezone,
} from 'use-intl/core';
import { EmbedMessageSyntaxError, parseEmbedMessage, POUND, type EmbedMessageElement } from './embedMessageSubset';

type Values = RichTranslationValues;
type Formatters = ReturnType<typeof _createIntlFormatters>;
/** What the module returns: React's `ReactNode`, as use-intl's types spell it. */
type FormatMessageResult = ReturnType<RichTagsFunction>;

/**
 * use-intl's signature for `use-intl/format-message` (its `FormatMessage`), spelt with what `use-intl/core` exports so
 * that every TypeScript setting resolves it; embedFormatMessage.test.ts holds it equal to the module's own type.
 */
export type FormatMessage = (
	key: string,
	message: string,
	values: Values | undefined,
	options: {
		cache: ReturnType<typeof _createCache>;
		formatters: Formatters;
		globalFormats?: Formats;
		formats?: Formats;
		locale: string;
		timeZone?: Timezone;
	},
) => FormatMessageResult;
type FormatMessageArgs = Parameters<FormatMessage>;

/** intl-messageformat's part: literal text, or an object (any other value an argument was given). */
type Part = { literal: true; value: string } | { literal: false; value: unknown };

/** Each message's elements, parsed once, as use-intl keeps each compiled message. */
const parsedMessages = new Map<string, EmbedMessageElement[]>();

/** The locales `IntlMessageFormat.resolveLocale` has accepted. */
const resolvedLocales = new Set<string>();

/**
 * React 19's `isValidElement`, which use-intl's module asks of the result. Written out rather than imported:
 * packages/i18n does not depend on React, and the embed's tests resolve this module from its own folder.
 */
function isReactElement(value: unknown): boolean {
	return typeof value === 'object' && value !== null && (value as { $$typeof?: unknown }).$$typeof === Symbol.for('react.transitional.element');
}

export default function formatMessage(...[key, message, values, options]: FormatMessageArgs): FormatMessageResult {
	// use-intl's development build words its errors, and compiles a message with an argument or a tag even without
	// values so that a missing value is reported; its production build does neither.
	const development = process.env.NODE_ENV !== 'production';
	const raw: unknown = message;
	if (Array.isArray(raw)) {
		throw new IntlError(
			IntlErrorCode.INVALID_MESSAGE,
			development
				? `Message at \`${key}\` resolved to an array, but only strings are supported. See https://next-intl.dev/docs/usage/translations#arrays-of-messages`
				: undefined,
		);
	}
	if (typeof raw === 'object') {
		throw new IntlError(
			IntlErrorCode.INSUFFICIENT_PATH,
			development
				? `Message at \`${key}\` resolved to \`${typeof raw}\`, but only strings are supported. Use a \`.\` to retrieve nested messages. See https://next-intl.dev/docs/usage/translations#structuring-messages`
				: undefined,
		);
	}
	// The hot path: without values a message is its own text, unless it quotes a brace.
	if (typeof raw === 'string' && !values && !/'[{}]/.test(raw) && !(development && /<|{/.test(raw)) && raw) return raw;

	const elements = compile(raw, options.locale, development);
	const result = format(elements, options.locale, options.formatters, values, raw as string);
	return (isReactElement(result) || Array.isArray(result) || typeof result === 'string' ? result : String(result)) as FormatMessageResult;
}
// `t.raw` hands out the message itself, which this module allows as use-intl's does.
formatMessage.raw = true;

/**
 * What `new IntlMessageFormat(message, locale)` checks, in its order: the locale (`resolveLocale`), then that the
 * message is a string, then its syntax. Any failure is INVALID_MESSAGE, as use-intl reports a message that does
 * not compile.
 */
function compile(message: unknown, locale: string, development: boolean): EmbedMessageElement[] {
	try {
		if (!resolvedLocales.has(locale) && typeof Intl.Locale !== 'undefined') {
			const supported = Intl.NumberFormat.supportedLocalesOf(locale);
			new Intl.Locale(supported.length > 0 ? supported[0] : locale);
			resolvedLocales.add(locale);
		}
		if (typeof message !== 'string') throw new TypeError('A message must be provided as a String or AST.');
		let elements = parsedMessages.get(message);
		if (!elements) {
			elements = parseEmbedMessage(message);
			parsedMessages.set(message, elements);
		}
		return elements;
	} catch (error) {
		throw new IntlError(IntlErrorCode.INVALID_MESSAGE, development ? `${compileErrorText(error)} (${String(message)})` : undefined);
	}
}

/** A compile error in words, for the development build's report. */
function compileErrorText(error: unknown): string {
	if (!(error instanceof EmbedMessageSyntaxError)) return error instanceof Error ? error.message : String(error);
	const what = error.construct === null ? 'Invalid ICU' : `ICU ${error.construct}, which the embed does not format,`;
	return `${what} at offset ${error.offset} (packages/i18n/src/embedMessageSubset.ts)`;
}

/** intl-messageformat's `format`: one part is its own value, adjacent texts join, and several parts stay a list. */
function format(elements: EmbedMessageElement[], locale: string, formatters: Formatters, values: Values | undefined, message: string): unknown {
	const parts = formatToParts(elements, locale, formatters, values, undefined, message);
	if (parts.length === 1) return parts[0].value;
	const result = parts.reduce<unknown[]>((all, part) => {
		if (!all.length || !part.literal || typeof all[all.length - 1] !== 'string') all.push(part.value);
		else all[all.length - 1] = (all[all.length - 1] as string) + part.value;
		return all;
	}, []);
	return result.length <= 1 ? result[0] || '' : result;
}

/**
 * intl-messageformat's `formatToParts` for the subset's elements. As there, a plural's or a select's option is
 * formatted without the message it belongs to, so a value missing inside one is reported for the string "undefined".
 */
function formatToParts(
	elements: EmbedMessageElement[],
	locale: string,
	formatters: Formatters,
	values: Values | undefined,
	pluralValue: number | undefined,
	message: string | undefined,
): Part[] {
	if (elements.length === 1 && typeof elements[0] === 'string') return [{ literal: true, value: elements[0] }];
	const parts: Part[] = [];
	for (const element of elements) {
		if (typeof element === 'string') {
			parts.push({ literal: true, value: element });
			continue;
		}
		if (element === POUND) {
			if (typeof pluralValue === 'number') parts.push({ literal: true, value: formatters.getNumberFormat(locale).format(pluralValue) });
			continue;
		}
		const { name } = element;
		if (!(values && name in values)) {
			// intl-messageformat's MissingValueError, word for word: use-intl reports its text as a FORMATTING_ERROR.
			throw new Error(`The intl string context variable "${name}" was not provided to the string "${message}"`);
		}
		const value: unknown = values[name];
		if (element.type === 'argument') {
			let text = value;
			if (!text || typeof text === 'string' || typeof text === 'number' || typeof text === 'bigint') {
				text = typeof text === 'string' || typeof text === 'number' || typeof text === 'bigint' ? String(text) : '';
			}
			parts.push(typeof text === 'string' ? { literal: true, value: text } : { literal: false, value: text });
			continue;
		}
		if (element.type === 'select') {
			// The option named by the value itself, else `other`, formatted without a plural's number.
			const option =
				(Object.prototype.hasOwnProperty.call(element.options, value as PropertyKey) ? element.options[value as string] : undefined) ||
				element.options.other;
			parts.push(...formatToParts(option, locale, formatters, values, undefined, undefined));
			continue;
		}
		// The `=N` option whose number is the value, else the value's plural category (after the offset), else `other`.
		// The value goes through JavaScript's own conversions (`=${value}`, `value - offset`), as it does there.
		const numericValue = (typeof value === 'bigint' ? Number(value) : value) as number;
		let option = element.options[`=${value as string}`];
		if (!option) {
			const rule = formatters.getPluralRules(locale, { type: 'cardinal' }).select(numericValue - (element.offset || 0));
			option = element.options[rule] || element.options.other;
		}
		parts.push(...formatToParts(option, locale, formatters, values, numericValue - (element.offset || 0), undefined));
	}
	return mergeLiteral(parts);
}

/** intl-messageformat's `mergeLiteral`: adjacent literal parts become one. */
function mergeLiteral(parts: Part[]): Part[] {
	if (parts.length < 2) return parts;
	return parts.reduce<Part[]>((all, part) => {
		const last = all[all.length - 1];
		if (!last || !last.literal || !part.literal) all.push(part);
		else last.value += part.value;
		return all;
	}, []);
}
